// CRM email (Blue Market only): write to a client's contacts, see what's on its way or failed, and file logged emails
// that matched no client. Sent and logged emails show up in the activity timeline.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, post } from '../api';
import { useAuth } from '../auth';
import { Icon, Modal, ago, useToast } from '../ui';
import type { Client, Contact } from '../types';

interface CrmEmail { id: string; client_id: string | null; direction: 'in' | 'out'; status: 'queued' | 'sent' | 'failed' | 'logged' | 'unmatched';
  from: string; to: string[]; cc: string[]; subject: string; body: string; sent_at: string | null; by: string; error: string; created_at: string }
interface ClientEmails { emails: CrmEmail[]; can_send: boolean; log_address: string }

export function EmailBar({ client, contacts }: { client: Client; contacts: Contact[] }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['crm-emails', client.id], queryFn: () => get<ClientEmails>(`/api/clients/${client.id}/emails`),
                              refetchInterval: (q) => q.state.data?.emails.some((e) => e.status === 'queued') ? 3000 : false });
  const [writing, setWriting] = useState(false);
  if (!data) return null;
  const retry = (e: CrmEmail) => post(`/api/emails/${e.id}/retry`, {}).then(() => qc.invalidateQueries({ queryKey: ['crm-emails', client.id] })).catch((er: Error) => toast(er.message, true));
  return (
    <>
      {(data.can_send || data.log_address) && (
        <div className="row wrap email-bar" style={{ gap: 10 }}>
          {data.can_send && <button className="btn sm" onClick={() => setWriting(true)} disabled={!contacts.some((c) => c.email && !c.archived)}
                                    title={contacts.some((c) => c.email) ? '' : 'Add a contact with an email address first'}><Icon name="send" className="ico sm" />Email…</button>}
          {data.log_address && <span className="muted tiny">To log an email here, BCC or forward it to <span className="mono">{data.log_address}</span></span>}
        </div>
      )}
      {data.emails.map((e) => (
        <div key={e.id} className={`email-pending ${e.status}`}>
          {e.status === 'queued' ? <span className="spin" /> : <Icon name="x" className="ico sm" />}
          <span className="grow small"><b>{e.subject}</b> <span className="muted">to {e.to.join(', ')}</span>
            {e.status === 'failed' && <span className="bad-text tiny" title={e.error}> · not sent</span>}</span>
          {e.status === 'failed' && <button className="btn sm ghost" onClick={() => retry(e)}>Send again</button>}
        </div>
      ))}
      {writing && <ComposeEmail client={client} contacts={contacts.filter((c) => c.email && !c.archived)} onClose={() => setWriting(false)} />}
    </>
  );
}

function ComposeEmail({ client, contacts, onClose }: { client: Client; contacts: Contact[]; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { me } = useAuth();
  const main = contacts.find((c) => c.primary) ?? contacts[0];
  const [f, setF] = useState({ to: main ? [main.id] : [] as string[], cc: [] as string[], subject: '', body: '', marketing: false, not_marketing: false });
  const picked = contacts.filter((c) => f.to.includes(c.id) || f.cc.includes(c.id));
  const optedOut = picked.filter((c) => c.marketing_consent === 'refused' || c.marketing_consent === 'withdrawn');
  const noConsent = picked.filter((c) => c.marketing_consent !== 'granted');
  const send = useMutation({
    mutationFn: () => post(`/api/clients/${client.id}/emails`, f),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['crm-emails', client.id] }); toast('Sending…'); onClose(); },
    onError: (e: Error) => toast(e.message, true),
  });
  const toggle = (list: 'to' | 'cc', id: string) => setF((x) => {
    const other = list === 'to' ? 'cc' : 'to';
    return { ...x, [list]: x[list].includes(id) ? x[list].filter((i) => i !== id) : [...x[list], id], [other]: x[other].filter((i) => i !== id) };
  });
  const blocked = !f.to.length || !f.subject.trim() || !f.body.trim() || (f.marketing && noConsent.length > 0) || (!f.marketing && optedOut.length > 0 && !f.not_marketing);
  return (
    <Modal title={`Email ${client.name}`} onClose={onClose} width={620}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); if (!blocked) send.mutate(); }}>
        {(['to', 'cc'] as const).map((list) => (
          <div key={list} className="field"><span>{list === 'to' ? 'To' : 'Cc'}</span>
            <div className="people-pick">{contacts.map((c) => (
              <button key={c.id} type="button" className={`fopt ${f[list].includes(c.id) ? 'on' : ''}`} aria-pressed={f[list].includes(c.id)} onClick={() => toggle(list, c.id)}
                      title={c.email ?? ''}>{c.name}</button>))}</div></div>
        ))}
        <label className="field"><span>Subject</span><input className="input" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} maxLength={200} required /></label>
        <label className="field"><span>Message</span><textarea className="input" rows={8} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} maxLength={20000} required /></label>
        <label className="row small" style={{ gap: 8 }}><input type="checkbox" checked={f.marketing} onChange={(e) => setF({ ...f, marketing: e.target.checked, not_marketing: false })} />
          This is direct marketing (a newsletter, an offer, a promotion)</label>
        {f.marketing && noConsent.length > 0 && <div className="callout bad">Direct marketing only goes to people who agreed to it (POPIA). Not agreed: {noConsent.map((c) => c.name).join(', ')}.</div>}
        {f.marketing && !noConsent.length && <p className="muted tiny" style={{ margin: 0 }}>A line telling them how to stop these emails is added at the end.</p>}
        {!f.marketing && optedOut.length > 0 && (
          <label className="callout warn row" style={{ gap: 8, alignItems: 'flex-start' }}>
            <input type="checkbox" checked={f.not_marketing} onChange={(e) => setF({ ...f, not_marketing: e.target.checked })} />
            <span>{optedOut.map((c) => c.name).join(', ')} opted out of marketing. Tick to confirm this is an ordinary business email about your work together, not marketing.</span>
          </label>
        )}
        <p className="muted tiny" style={{ margin: 0 }}>Sent as "{me?.name} at Blue Market"; replies come to {me?.email}. It's added to the activity timeline once sent.</p>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}><button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={blocked || send.isPending}>{send.isPending && <span className="spin" />}Send</button></div>
      </form>
    </Modal>
  );
}

/** Logged emails that matched no single client: on the Clients page, only when there are some. */
export function UnfiledEmails({ clients }: { clients: Client[] }) {
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['unfiled'], queryFn: () => get<{ emails: CrmEmail[] }>('/api/crm/emails/unfiled'), enabled: can('crm.view') });
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<Record<string, string>>({});
  const n = data?.emails.length ?? 0;
  if (!n) return null;
  const done = () => { qc.invalidateQueries({ queryKey: ['unfiled'] }); qc.invalidateQueries({ queryKey: ['activities'] }); };
  const file = (e: CrmEmail) => post(`/api/crm/emails/${e.id}/file`, { client_id: target[e.id] }).then(() => { done(); toast('Filed'); }).catch((er: Error) => toast(er.message, true));
  const discard = (e: CrmEmail) => del(`/api/crm/emails/${e.id}`).then(done).catch((er: Error) => toast(er.message, true));
  return (
    <>
      <div className="callout warn row" style={{ gap: 10 }}><span className="grow">{n} logged email{n === 1 ? '' : 's'} couldn't be matched to a client.</span>
        <button className="btn sm" onClick={() => setOpen(true)}>File them</button></div>
      {open && (
        <Modal title="Unfiled emails" onClose={() => setOpen(false)} width={640}>
          <div className="stack">
            {data!.emails.map((e) => (
              <div key={e.id} className="card pad stack" style={{ gap: 6 }}>
                <div className="small"><b>{e.subject || '(no subject)'}</b> <span className="muted tiny">· logged by {e.by} {ago(e.created_at)}</span></div>
                <div className="muted tiny">From {e.from} to {e.to.join(', ') || '–'}</div>
                <div className="small email-preview">{e.body.slice(0, 300)}</div>
                {can('crm.edit') && <div className="row" style={{ gap: 6 }}>
                  <select className="input compact grow" value={target[e.id] ?? ''} onChange={(ev) => setTarget({ ...target, [e.id]: ev.target.value })} aria-label="Client">
                    <option value="">File under…</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
                  <button className="btn sm primary" disabled={!target[e.id]} onClick={() => file(e)}>File</button>
                  <button className="btn sm ghost" onClick={() => discard(e)}>Discard</button></div>}
              </div>
            ))}
          </div>
        </Modal>
      )}
    </>
  );
}
